import React, { useRef, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import { updateAssociation } from "../../services/associationService";
import { AssociationsFetchResult } from "../../store/associationContext-actions";
import {
  AssociationRequest,
  AssociationResponse,
} from "../../types/association.types";
import {
  isAssociationNotAdministered,
  mapAssociationSaveError,
  shouldRefetchAssociationsAfterError,
} from "../../utils/associationErrors";
import styles from "./AssociationProfilePage.module.scss";

// Association portal, slice 2: publication. An unpublished association
// leaves the consumer list and stays in `/association/mine`.

export const PUBLICATION_HINT =
  "Neobjavljeno udruženje nije vidljivo korisnicima, ali ostaje dostupno vama.";
export const PUBLISHED_STATUS = "Status: Objavljeno";
export const UNPUBLISHED_STATUS = "Status: Nije objavljeno";

/**
 * The toggle rides the same PUT as the data form, which overwrites every
 * scalar. The scalars sent are the SERVER's current values (the context row),
 * never the data form's unsaved edits, so a toggle cannot smuggle in a
 * half-typed change. Media and opening hours are left untouched.
 */
export const buildPublicationRequest = (
  association: AssociationResponse,
): AssociationRequest => ({
  name: association.name,
  description: association.description,
  address: association.address,
  city: association.city,
  postalCode: association.postalCode,
  phoneNumber: association.phoneNumber,
  latitude: association.latitude,
  longitude: association.longitude,
  categoryIds: (association.categories ?? []).map((category) => category.id),
  active: !association.active,
  updateImages: false,
  workingHours: null,
});

export interface PublicationCardProps {
  association: AssociationResponse;
  /** Another write on this page is in flight: the toggle waits for it. */
  busy: boolean;
  onPendingChange: (pending: boolean) => void;
  /** The page shows the copy and re-reads `/mine`. */
  onNotAdministered: () => void;
  refetch: () => Promise<AssociationsFetchResult>;
}

const PublicationCard: React.FC<PublicationCardProps> = ({
  association,
  busy,
  onPendingChange,
  onNotAdministered,
  refetch,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);

  const toggle = async () => {
    if (savingRef.current || busy) {
      return;
    }
    const request = buildPublicationRequest(association);
    savingRef.current = true;
    setSaving(true);
    onPendingChange(true);
    setError(null);
    try {
      await updateAssociation(association.id, request);
      // The state shown is the context row, so the re-read is what flips it.
      await refetch();
    } catch (failure) {
      if (isAssociationNotAdministered(failure)) {
        onNotAdministered();
        return;
      }
      setError(mapAssociationSaveError(failure));
      if (shouldRefetchAssociationsAfterError(failure)) {
        void refetch();
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      onPendingChange(false);
    }
  };

  const published = association.active;

  return (
    <section
      className={styles.card}
      aria-labelledby="association-publication-title"
    >
      <h2 id="association-publication-title" className={styles.cardTitle}>
        Objava
      </h2>
      <p
        className={`${styles.publicationStatus} ${
          published ? styles.published : ""
        }`}
      >
        {published ? PUBLISHED_STATUS : UNPUBLISHED_STATUS}
      </p>
      <p className={styles.hint}>{PUBLICATION_HINT}</p>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Button
        buttonType={published ? "white" : "blue"}
        disabled={saving || busy}
        onClick={toggle}
      >
        {saving ? "Slanje..." : published ? "Povuci iz objave" : "Objavi"}
      </Button>
    </section>
  );
};

export default PublicationCard;
